import React, { useEffect, useState } from "react";
import { Save, RefreshCw, Plus, Trash2, ToggleLeft, ToggleRight, Globe, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const DAY_ORDER = ["Måndag", "Tisdag", "Onsdag", "Torsdag", "Fredag", "Lördag", "Söndag"];

function emptyDays() {
  return DAY_ORDER.map((name) => ({ name, date: "", price: 120, note: "", dishes: [] }));
}

export default function LunchEditor() {
  const [override, setOverride] = useState(null);
  const [scraped, setScraped] = useState(null);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    const [o, s] = await Promise.all([
      api.get("/admin/lunch/override"),
      api.get("/lunch"),
    ]);
    const data = o.data || {};
    if (!data.days || data.days.length === 0) {
      data.days = emptyDays();
    }
    setOverride(data);
    setScraped(s.data);
  };

  useEffect(() => {
    load();
  }, []);

  const updateDay = (i, patch) => {
    setOverride((o) => {
      const days = [...o.days];
      days[i] = { ...days[i], ...patch };
      return { ...o, days };
    });
  };

  const addDish = (i) => updateDay(i, { dishes: [...override.days[i].dishes, { name: "", description: "" }] });
  const removeDish = (i, j) => {
    const arr = [...override.days[i].dishes];
    arr.splice(j, 1);
    updateDay(i, { dishes: arr });
  };
  const setDish = (i, j, patch) => {
    const arr = [...override.days[i].dishes];
    const cur = typeof arr[j] === "string" ? { name: arr[j], description: "" } : arr[j];
    arr[j] = { ...cur, ...patch };
    updateDay(i, { dishes: arr });
  };

  const save = async () => {
    setSaving(true);
    try {
      const days = override.days.map((d) => ({
        ...d,
        price: d.price === "" || d.price == null ? null : Number(d.price),
        dishes: (d.dishes || [])
          .map((s) => (typeof s === "string" ? { name: s.trim(), description: "" } : { name: (s.name || "").trim(), description: (s.description || "").trim() }))
          .filter((s) => s.name),
      }));
      await api.put("/admin/lunch/override", {
        enabled: !!override.enabled,
        week: override.week ? Number(override.week) : null,
        days,
      });
      toast.success(override.enabled ? "Manuell lunch aktiverad" : "Sparat (avstängt – matochmat.se används)");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte spara");
    } finally {
      setSaving(false);
    }
  };

  const refreshScraped = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/admin/lunch/refresh");
      setScraped(data);
      toast.success("Hämtade ny lunchmeny från matochmat.se");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte hämta");
    } finally {
      setRefreshing(false);
    }
  };

  const importScraped = () => {
    if (!scraped?.days?.length) return toast.error("Ingen lunch hämtad än");
    const merged = DAY_ORDER.map((name) => {
      const s = scraped.days.find((d) => d.name === name);
      return s
        ? {
            name,
            date: s.date || "",
            price: s.price ?? 120,
            note: s.note || "",
            dishes: (s.dishes || []).map((x) =>
              typeof x === "string"
                ? { name: x, description: "" }
                : { name: x.name || "", description: x.description || "" }
            ),
          }
        : { name, date: "", price: 120, note: "", dishes: [] };
    });
    setOverride((o) => ({ ...o, days: merged, week: scraped.week ?? o.week }));
    toast.success("Importerade från matochmat.se – glöm inte spara");
  };

  if (!override) return <div className="py-12 text-center text-[var(--text-muted)]">Laddar…</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-serif-display text-3xl sm:text-4xl font-black">Dagens lunch</h1>
          <p className="text-[var(--text-muted)] mt-1">
            Skriv över lunchmenyn manuellt eller låt sidan hämta från matochmat.se automatiskt.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={refreshScraped}
            disabled={refreshing}
            data-testid="lunch-refresh-button"
            className="inline-flex items-center gap-2 rounded-full bg-white border border-[var(--border)] hover:border-[var(--text)] px-4 py-2 text-sm font-semibold disabled:opacity-50"
          >
            <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} /> Uppdatera matochmat.se
          </button>
          <button
            onClick={importScraped}
            data-testid="lunch-import-button"
            className="inline-flex items-center gap-2 rounded-full bg-white border border-[var(--border)] hover:border-[var(--text)] px-4 py-2 text-sm font-semibold"
          >
            <Globe size={14} /> Importera till manuell
          </button>
        </div>
      </div>

      {/* Toggle */}
      <div className="bg-white border border-[var(--border)] rounded-2xl p-5 flex items-center gap-4">
        <button
          onClick={() => setOverride({ ...override, enabled: !override.enabled })}
          data-testid="lunch-override-toggle"
          className="shrink-0"
          aria-label="Aktivera manuell lunch"
        >
          {override.enabled ? (
            <ToggleRight size={42} className="text-[var(--primary)]" />
          ) : (
            <ToggleLeft size={42} className="text-stone-300" />
          )}
        </button>
        <div className="flex-1 min-w-0">
          <div className="font-semibold">
            {override.enabled ? "Manuell lunch är AKTIV" : "Använder matochmat.se automatiskt"}
          </div>
          <div className="text-sm text-[var(--text-muted)]">
            {override.enabled
              ? "Det du skriver nedan visas på hemsidan – matochmat.se ignoreras."
              : "Aktivera för att helt kontrollera lunchmenyn själv."}
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-2 text-sm">
          <Pencil size={14} className="text-[var(--text-muted)]" />
          <input
            type="number"
            placeholder="Vecka"
            value={override.week ?? ""}
            onChange={(e) => setOverride({ ...override, week: e.target.value })}
            data-testid="lunch-week-input"
            className="w-20 bg-white border border-[var(--border)] rounded-lg px-3 py-1.5 text-sm"
          />
        </div>
      </div>

      {/* Days */}
      <div className="space-y-4">
        {override.days.map((d, i) => (
          <div key={d.name} className="bg-white border border-[var(--border)] rounded-2xl p-5" data-testid={`lunch-day-${i}`}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
              <h3 className="font-serif-display text-2xl font-bold">{d.name}</h3>
              <div className="flex flex-wrap gap-2 items-center">
                <input
                  type="text"
                  placeholder="20/4"
                  value={d.date || ""}
                  onChange={(e) => updateDay(i, { date: e.target.value })}
                  className="w-20 bg-white border border-[var(--border)] rounded-lg px-3 py-1.5 text-sm"
                  data-testid={`lunch-day-${i}-date`}
                />
                <input
                  type="number"
                  placeholder="Pris"
                  value={d.price ?? ""}
                  onChange={(e) => updateDay(i, { price: e.target.value })}
                  className="w-20 bg-white border border-[var(--border)] rounded-lg px-3 py-1.5 text-sm"
                  data-testid={`lunch-day-${i}-price`}
                />
                <span className="text-xs text-[var(--text-muted)]">kr</span>
              </div>
            </div>
            <input
              type="text"
              placeholder="Anteckning (t.ex. inkl. sallader, eller 'Lunchstängt')"
              value={d.note || ""}
              onChange={(e) => updateDay(i, { note: e.target.value })}
              className="w-full bg-white border border-[var(--border)] rounded-lg px-3 py-2 text-sm mb-3"
              data-testid={`lunch-day-${i}-note`}
            />
            <div className="space-y-3">
              {(d.dishes || []).map((dish, j) => {
                const cur = typeof dish === "string" ? { name: dish, description: "" } : dish;
                return (
                  <div key={`${d.day || i}-${cur.name || ""}-${j}`} className="flex gap-2 items-start bg-[var(--surface-alt)] rounded-lg p-2">
                    <div className="flex-1 space-y-1.5">
                      <input
                        type="text"
                        value={cur.name || ""}
                        onChange={(e) => setDish(i, j, { name: e.target.value })}
                        placeholder={`Rätt ${j + 1} – t.ex. Hemlagade köttbullar`}
                        className="w-full bg-white border border-[var(--border)] rounded-lg px-3 py-2 text-sm font-semibold focus:outline-none focus:border-[var(--primary)]"
                        data-testid={`lunch-day-${i}-dish-${j}-name`}
                      />
                      <input
                        type="text"
                        value={cur.description || ""}
                        onChange={(e) => setDish(i, j, { description: e.target.value })}
                        placeholder="Beskrivning – t.ex. med potatismos, gräddsås, lingonsylt"
                        className="w-full bg-white border border-[var(--border)] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[var(--primary)]"
                        data-testid={`lunch-day-${i}-dish-${j}-desc`}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeDish(i, j)}
                      className="w-9 h-9 grid place-items-center rounded-lg border border-[var(--border)] bg-white hover:border-red-400 hover:text-red-600 shrink-0"
                      aria-label="Ta bort rätt"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })}
              <button
                type="button"
                onClick={() => addDish(i)}
                data-testid={`lunch-day-${i}-add`}
                className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--primary)] hover:text-[var(--primary-hover)]"
              >
                <Plus size={14} /> Lägg till rätt
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="sticky bottom-4 flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          data-testid="lunch-save-button"
          className="inline-flex items-center gap-2 rounded-full bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white px-6 py-3 font-bold shadow-lg shadow-[var(--primary)]/30 disabled:opacity-60"
        >
          <Save size={16} /> {saving ? "Sparar…" : "Spara lunchmeny"}
        </button>
      </div>
    </div>
  );
}
